import React, { useCallback, type KeyboardEvent } from 'react';
import cx from 'classnames';
import { CheckmarkPartial } from '@design-systems/icons';
import useId from '@mc/hooks/useId';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import { ariaDescribedByIds } from '../utils';
import { isValidLogo, LOGO_MAP } from '../Coin/logo-map';
import ChoiceCardGroup, {
  useChoiceOnChange,
  useChoiceSelectedValues,
  useChoiceInputTypeValue,
} from './ChoiceCardGroup';

import stylesheet from './ChoiceCard.css';
import { useColorScheme } from './../../helpers/utils-ts';

export type ChoiceCardProps = {
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  image?: string | React.ReactElement;
  label: React.ReactNode;
  value?: string | number | boolean;
  forceChecked?: boolean;
  orientation?: 'horizontal' | 'vertical';
  /** Will show in place of help text if defined also applies invalid style treatment. */
  fieldError?: string;
};

const getIntegrationEntryConfig = (integration: string) => {
  return LOGO_MAP[integration];
};

/** Choice Card is a form element used when only one item can be selected from a group of two or more items in a list of options. */
const ChoiceCard = React.forwardRef<$TSFixMe, ChoiceCardProps>(
  function ChoiceCard(
    {
      className,
      children,
      id,
      image,
      label,
      value,
      orientation = 'horizontal',
      disabled = false,
      forceChecked = false,
      fieldError,
      ...props
    },
    forwardedRef,
  ) {
    const selectedValues = useChoiceSelectedValues();
    const onChange = useChoiceOnChange();
    const colorScheme = useColorScheme();
    /* For accessibility, error messages have better support when associated to form controls vs fieldset/legends.
     * Pulling in error id to associate it with aria-describedby.
     * Reference: https://www.last-child.com/legend-aria-describedby/
     */
    // @ts-expect-error TS(2339) FIXME: Property 'inputType' does not exist on type 'Symbo... Remove this comment to see the full error message
    const { inputType, errorId, error } = useChoiceInputTypeValue();
    const inputId = useId();
    const labelId = useId();
    const descriptionId = useId();
    const fieldErrorId = useId();
    const describedBy = ariaDescribedByIds(
      children && descriptionId,
      error && errorId,
    );
    const [type, hidden] = inputType.split('-');
    id = id || inputId;
    let logo;
    let logoLabel;
    if (image && typeof image === 'string' && isValidLogo(image)) {
      logo = getIntegrationEntryConfig(image);
      logoLabel = `${logo?.label}`;
    }

    const isSelected = useCallback(() => {
      return Array.isArray(selectedValues)
        ? selectedValues.includes(value)
        : // @ts-expect-error TS(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
          selectedValues === value;
    }, [selectedValues, value]);

    const isOrganismsPart2FlagOn = mcdsFlagCheck(
      'xp_mcds_redesign_components_organisms_part_2',
    );

    const handleKeyDown = (
      newValue: string | number | boolean | undefined,
      event: KeyboardEvent<HTMLInputElement>,
    ) => {
      if (disabled) {
        return;
      }

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (!isSelected()) {
            // @ts-expect-error TS(2349) FIXME: This expression is not callable.
            onChange(newValue);
          }
          break;
        default:
          break;
      }
    };

    return (
      <div
        className={cx(stylesheet.choice, className, {
          [stylesheet.checked]: isSelected(),
          [stylesheet.disabled]: disabled,
          [stylesheet.simple]: !!hidden,
          [stylesheet.hideInput]: image,
          [stylesheet.error]: !!error || fieldError,
        })}
      >
        <label
          htmlFor={id}
          className={cx(
            stylesheet.card,
            isOrganismsPart2FlagOn && stylesheet[orientation],
          )}
          {...props}
        >
          {isOrganismsPart2FlagOn ? (
            <>
              <div className={image ? stylesheet.cardCenterAlign : ''}>
                <input
                  className={stylesheet.input}
                  id={id}
                  type={type}
                  // @ts-expect-error TS(2322) FIXME: Type 'string | number | boolean | undefined' is no... Remove this comment to see the full error message
                  value={value}
                  checked={isSelected() || forceChecked}
                  // @ts-expect-error TS(2349) FIXME: This expression is not callable.
                  onChange={() => onChange(value)}
                  onKeyDown={(event) => handleKeyDown(value, event)}
                  disabled={disabled}
                  ref={forwardedRef}
                  aria-describedby={describedBy}
                  aria-labelledby={labelId}
                />

                {image ? (
                  typeof image === 'string' ? (
                    <div className={stylesheet.imageContainer}>
                      <img
                        src={
                          isValidLogo(image)
                            ? colorScheme === 'dark'
                              ? logo?.image.dark ?? logo?.image.light
                              : logo?.image.light
                            : image
                        }
                        alt={isValidLogo(image) ? logoLabel : ''}
                        className={stylesheet.image}
                      ></img>
                    </div>
                  ) : (
                    <div className={stylesheet.imageContainer}>{image}</div>
                  )
                ) : !hidden ? (
                  <div
                    className={cx(stylesheet.checkmarkContainer, {
                      [stylesheet.checkmarkContainerSelected]:
                        isSelected() || forceChecked,
                    })}
                  >
                    <CheckmarkPartial />
                  </div>
                ) : null}
              </div>
              <div className={stylesheet.contentContainer}>
                <div
                  className={hidden ? stylesheet.simpleLabel : stylesheet.label}
                  id={labelId}
                >
                  {label}
                </div>
                {children && (
                  <div id={descriptionId} className={stylesheet.description}>
                    {children}
                  </div>
                )}
                {error && (
                  <div id={fieldErrorId} className={stylesheet.errorMessage}>
                    {error}
                  </div>
                )}
                {fieldError && (
                  <div id={errorId} className={stylesheet.errorMessage}>
                    {fieldError}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <input
                className={stylesheet.input}
                id={id}
                type={type}
                // @ts-expect-error TS(2322) FIXME: Type 'string | number | boolean | undefined' is no... Remove this comment to see the full error message
                value={value}
                checked={isSelected()}
                // @ts-expect-error TS(2349) FIXME: This expression is not callable.
                onChange={() => onChange(value)}
                disabled={disabled}
                ref={forwardedRef}
                aria-describedby={describedBy}
                aria-labelledby={labelId}
              />
              <div>
                {typeof image === 'string' && isValidLogo(image) ? (
                  <img
                    src={
                      colorScheme === 'dark'
                        ? logo?.image.dark ?? logo?.image.light
                        : logo?.image.light
                    }
                    alt={logoLabel}
                    className={stylesheet.image}
                  />
                ) : typeof image === 'string' ? (
                  <img src={image} alt="" className={stylesheet.image}></img>
                ) : (
                  image
                )}
              </div>
              <div
                className={hidden ? stylesheet.simpleLabel : stylesheet.label}
                id={labelId}
              >
                {label}
              </div>
              {children && (
                <div id={descriptionId} className={stylesheet.description}>
                  {children}
                </div>
              )}
            </>
          )}
        </label>
      </div>
    );
  },
);

export { ChoiceCard as default, ChoiceCardGroup };
